import { useState, useEffect, useMemo, useCallback } from "react";
import useAuth from "../../../../Hooks/useAuth";
import usePermission from "../../../../Hooks/usePermission";
import { useAlert } from "../../../../context/AlertContext";
import { FaTags, FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import {
  useAddProductTypeMutation,
  useDeleteProductTypeMutation,
  useFetchProductTypeQuery,
  useUpdateProductTypeMutation,
} from "../../../../store";
import FormatDateHourString from "../../../../Components/Utils/FormatDateHourString";
import { TextInput, Pagination } from "../../../../Components/atoms";
import { ThreeDots } from "react-loader-spinner";
import PageShell from "../../../../Components/shared/PageShell";
import PageHeader from "../../../../Components/shared/PageHeader";
import SearchInput from "../../../../Components/shared/SearchInput";
import ListSkeleton from "../../../../Components/shared/ListSkeleton";
import EmptyState from "../../../../Components/shared/EmptyState";
import Modal from "../../../../Components/shared/Modal";

const ITEMS_PER_PAGE = 10;
const SKELETON_COLS = [
  { label: "Ürün Tipi", skeletonWidth: "w-32" },
  { label: "Değiştirilme", skeletonWidth: "w-24" },
  { label: "İşlemler", skeletonWidth: "w-16" },
];

export default function ProductTypePage() {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const { showSuccess, showError } = useAlert();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("products.typeCreate");
  const canEdit = hasPermission("products.typeEdit");
  const canDelete = hasPermission("products.typeDelete");

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [typeName, setTypeName] = useState("");

  const ProductType = useFetchProductTypeQuery(token);
  const [Update, resultUpdate] = useUpdateProductTypeMutation();
  const [Delete, resultDelete] = useDeleteProductTypeMutation();
  const [Add, resultAdd] = useAddProductTypeMutation();

  const types = useMemo(
    () => ProductType?.data?.data?.productTypes || [],
    [ProductType?.data?.data?.productTypes]
  );

  const filteredTypes = useMemo(() => {
    if (!searchTerm) return types;
    return types.filter((t) =>
      t.productTypeName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [types, searchTerm]);

  const paginatedTypes = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredTypes.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTypes, currentPage]);

  const totalPages = Math.ceil(filteredTypes.length / ITEMS_PER_PAGE);

  useEffect(() => setCurrentPage(1), [searchTerm]);

  const getErrMsg = useCallback(
    (err, fallback) => err?.data?.message || err?.error || fallback,
    []
  );

  const closeModals = useCallback(() => {
    setShowModal(false);
    setShowDeleteModal(false);
    setSelectedType(null);
    setTypeName("");
  }, []);

  useEffect(() => {
    if (resultAdd.isSuccess) { showSuccess("Ürün tipi eklendi!"); closeModals(); }
    else if (resultAdd.isError) showError(getErrMsg(resultAdd.error, "Ekleme başarısız!"));
  }, [resultAdd.isSuccess, resultAdd.isError]);

  useEffect(() => {
    if (resultUpdate.isSuccess) { showSuccess("Ürün tipi güncellendi!"); closeModals(); }
    else if (resultUpdate.isError) showError(getErrMsg(resultUpdate.error, "Güncelleme başarısız!"));
  }, [resultUpdate.isSuccess, resultUpdate.isError]);

  useEffect(() => {
    if (resultDelete.isSuccess) { showSuccess("Ürün tipi silindi!"); closeModals(); }
    else if (resultDelete.isError) showError(getErrMsg(resultDelete.error, "Silme başarısız!"));
  }, [resultDelete.isSuccess, resultDelete.isError]);

  const openAdd = () => { setSelectedType(null); setTypeName(""); setShowModal(true); };
  const openEdit = (t) => { setSelectedType(t); setTypeName(t.productTypeName); setShowModal(true); };
  const openDelete = (t) => { setSelectedType(t); setShowDeleteModal(true); };

  const handleSave = () => {
    if (!typeName.trim()) return;
    if (selectedType) Update({ productTypeName: typeName, id: selectedType._id, token });
    else Add({ productTypeName: typeName, token });
  };

  const isLoading = ProductType.isFetching;

  return (
    <PageShell>
      <div className="p-3 sm:p-4 border-b border-gray-100 flex flex-col gap-3">
        <PageHeader
          icon={FaTags}
          iconColor="amber"
          title="Ürün Tipi Yönetimi"
          subtitle="Ürün tiplerini görüntüleyin ve yönetin"
          stats={[{ label: "Toplam", value: types.length, color: "amber" }]}
          actions={canCreate ? [{ label: "Yeni Tip", icon: FaPlus, onClick: openAdd, variant: "primary" }] : []}
          isLoading={isLoading}
        />
        <div className="flex items-center gap-2">
          <SearchInput
            placeholder="Ürün tipi ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            wrapperClassName="flex-1 max-w-sm"
          />
          {canCreate && (
            <button onClick={openAdd} className="sm:hidden p-2.5 bg-amber-600 text-white rounded-xl shadow-sm">
              <FaPlus className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {isLoading ? (
          <ListSkeleton columns={SKELETON_COLS} rowCount={8} />
        ) : filteredTypes.length === 0 ? (
          <EmptyState
            icon={FaTags}
            message="Ürün tipi bulunamadı"
            suggestion={searchTerm ? "Farklı bir arama terimi deneyin." : "Henüz ürün tipi eklenmemiş."}
          />
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden p-3 space-y-2">
              {paginatedTypes.map((t) => (
                <div key={t._id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{t.productTypeName}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{FormatDateHourString(t.updatedInfo)}</p>
                  </div>
                  {(canEdit || canDelete) && (
                    <div className="flex gap-1 shrink-0 ml-2">
                      {canEdit && <button onClick={() => openEdit(t)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><FaEdit className="w-4 h-4" /></button>}
                      {canDelete && <button onClick={() => openDelete(t)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><FaTrash className="w-4 h-4" /></button>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ürün Tipi</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Değiştirilme Tarihi</th>
                    {(canEdit || canDelete) && <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">İşlemler</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedTypes.map((t) => (
                    <tr key={t._id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">{t.productTypeName}</td>
                      <td className="px-6 py-3 text-sm text-gray-500">{FormatDateHourString(t.updatedInfo)}</td>
                      {(canEdit || canDelete) && (
                        <td className="px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canEdit && <button onClick={() => openEdit(t)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><FaEdit className="w-4 h-4" /></button>}
                            {canDelete && <button onClick={() => openDelete(t)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><FaTrash className="w-4 h-4" /></button>}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {!isLoading && filteredTypes.length > ITEMS_PER_PAGE && (
        <div className="border-t border-gray-100 p-3">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            totalItems={filteredTypes.length}
            showInfo
          />
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal open={showModal} onClose={closeModals} maxWidth="sm">
        <Modal.Header title={selectedType ? "Ürün Tipi Düzenle" : "Yeni Ürün Tipi Ekle"} onClose={closeModals} />
        <Modal.Body>
          <TextInput
            label="Ürün Tipi Adı"
            value={typeName}
            onChange={(e) => setTypeName(e.target.value)}
            placeholder="Ürün tipi adını girin"
            required
          />
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-3">
          <button onClick={closeModals} className="px-4 py-2 text-gray-600 hover:text-gray-800">İptal</button>
          <button onClick={handleSave} disabled={resultAdd.isLoading || resultUpdate.isLoading} className="px-5 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2">
            {(resultAdd.isLoading || resultUpdate.isLoading) ? <ThreeDots height={16} width={16} color="white" /> : selectedType ? "Güncelle" : "Ekle"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal open={showDeleteModal} onClose={closeModals} maxWidth="sm">
        <Modal.Header title="Ürün Tipi Sil" onClose={closeModals} />
        <Modal.Body>
          <p className="text-gray-600"><strong>{selectedType?.productTypeName}</strong> ürün tipini silmek istediğinize emin misiniz?</p>
          <p className="text-sm text-red-600 mt-2">Bu işlem geri alınamaz!</p>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-3">
          <button onClick={closeModals} className="px-4 py-2 text-gray-600 hover:text-gray-800">İptal</button>
          <button onClick={() => Delete({ id: selectedType._id, token })} disabled={resultDelete.isLoading} className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
            {resultDelete.isLoading ? <ThreeDots height={16} width={16} color="white" /> : "Sil"}
          </button>
        </Modal.Footer>
      </Modal>
    </PageShell>
  );
}
